// ─── The selected day's bookings ─────────────────────────────────────────────
//
// Desktop: a right-hand panel beside the month grid. Mobile: the same
// component below the calendar. One implementation, because "the day's cards
// appear below the calendar when pressed, not in a side panel" is a placement
// decision, not a different feature.
//
// No faces, no avatars, no decorative imagery — the design doc rules those out
// of the calendar unless a later product genuinely needs them. What a business
// needs at a glance is who, what, when and whether it is confirmed.

import type { BookingStatus } from "./types.js";

export type DayBooking = {
  id: string;
  startsAt: Date;
  endsAt: Date;
  status: BookingStatus;
  /** Resolved by the application from @nulife/people — Booking itself stores
   *  only an id, so a name reaching this component has been looked up
   *  deliberately. */
  personName?: string | null;
  serviceName?: string | null;
  priceMinor?: number | null;
  currency?: string | null;
  notes?: string | null;
  blockReason?: string | null;
};

export type DayPanelProps = {
  date: Date;
  bookings: DayBooking[];
  onOpen?: (bookingId: string) => void;
  /** Rendered when the day has nothing on it — a business looking at an empty
   *  Tuesday should be told it is empty, not shown a blank rectangle. */
  emptyMessage?: string;
};

const time = (d: Date) =>
  d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });

const minutes = (a: Date, b: Date) => Math.round((b.getTime() - a.getTime()) / 60000);

function money(minor?: number | null, currency?: string | null): string | null {
  if (minor == null) return null;
  return new Intl.NumberFormat("en-GB", {
    style: "currency", currency: currency ?? "GBP",
  }).format(minor / 100);
}

const STATUS_LABEL: Record<BookingStatus, string> = {
  requested: "Requested",
  confirmed: "Confirmed",
  declined: "Declined",
  cancelled: "Cancelled",
  reschedule_requested: "Reschedule requested",
  expired: "Expired",
  blocked: "Blocked",
};

export function DayPanel({ date, bookings, onOpen, emptyMessage }: DayPanelProps) {
  const ordered = [...bookings].sort((a, b) => a.startsAt.getTime() - b.startsAt.getTime());

  return (
    <section
      data-testid="day-panel"
      aria-label={`Bookings on ${date.toDateString()}`}
      style={{ display: "flex", flexDirection: "column", gap: 10 }}
    >
      <header>
        <div style={{ fontSize: "0.72rem", letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--nl-ink-faint)" }}>
          {date.toLocaleDateString("en-GB", { weekday: "long" })}
        </div>
        <div style={{ fontSize: "1.15rem", color: "var(--nl-ink)" }}>
          {date.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
        </div>
      </header>

      {ordered.length === 0 && (
        <p style={{ color: "var(--nl-ink-dim)", fontSize: "0.9rem", margin: 0 }}>
          {emptyMessage ?? "Nothing booked."}
        </p>
      )}

      {ordered.map(b => {
        const isBlock = b.status === "blocked";
        return (
          <article
            key={b.id}
            data-testid="day-booking"
            data-status={b.status}
            style={{
              borderRadius: "var(--nl-radius-card)",
              border: "1px solid var(--nl-stroke)",
              background: "var(--nl-surface-raised)",
              padding: "10px 12px",
              display: "flex", flexDirection: "column", gap: 4,
            }}
          >
            {/* A status stripe, but never the only signal — the words are
                below it. Colour alone would exclude anyone who cannot
                separate these hues. */}
            <div
              aria-hidden="true"
              style={{
                height: 3, borderRadius: 2, marginBottom: 4,
                background: isBlock ? "var(--nl-state-blocked-ink)" : `var(--nl-status-${b.status})`,
                opacity: 0.9,
              }}
            />
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "baseline" }}>
              <span style={{ fontFamily: "var(--nl-font-num)", fontVariantNumeric: "tabular-nums", color: "var(--nl-ink)" }}>
                {time(b.startsAt)}–{time(b.endsAt)}
              </span>
              <span style={{ fontSize: "0.75rem", color: "var(--nl-ink-faint)" }}>
                {minutes(b.startsAt, b.endsAt)} min
              </span>
            </div>

            <div style={{ color: "var(--nl-ink)", fontSize: "1rem" }}>
              {isBlock ? (b.blockReason ?? "Blocked") : (b.personName ?? "Client")}
            </div>

            {!isBlock && b.serviceName && (
              <div style={{ color: "var(--nl-ink-dim)", fontSize: "0.85rem" }}>{b.serviceName}</div>
            )}

            <div style={{ display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap" }}>
              <span style={{ fontSize: "0.72rem", letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--nl-ink-faint)" }}>
                {STATUS_LABEL[b.status]}
              </span>
              {money(b.priceMinor, b.currency) && (
                <span style={{ fontSize: "0.8rem", color: "var(--nl-ink-dim)" }}>
                  {money(b.priceMinor, b.currency)}
                </span>
              )}
            </div>

            {b.notes && (
              <p style={{ margin: "2px 0 0", fontSize: "0.82rem", color: "var(--nl-ink-dim)" }}>{b.notes}</p>
            )}

            {onOpen && (
              <button
                type="button"
                onClick={() => onOpen(b.id)}
                style={{
                  marginTop: 6, alignSelf: "flex-start",
                  borderRadius: "var(--nl-radius-control)",
                  border: "1px solid var(--nl-stroke)",
                  background: "var(--nl-state-open)",
                  color: "var(--nl-state-open-ink)",
                  minHeight: "var(--nl-hit-min)",
                  padding: "0 14px", cursor: "pointer", font: "inherit",
                }}
              >Open</button>
            )}
          </article>
        );
      })}
    </section>
  );
}
